import type { ICard, ICardFillJob } from '@/entities/client.entity';

export { isCardBalanceFetchReady } from '@crave/config/card-fill-balance-ready';

export const CARD_FILL_JOB_TYPE_LABELS: Record<string, string> = {
	phone_lookup: 'Поиск карт по номеру',
	date_check: 'Проверка срока / ФИО',
	pan_resolve: 'Подбор PAN по маске',
	balance_fetch: 'Обновление баланса',
};

export const CARD_FILL_JOB_STATUS_LABELS: Record<string, string> = {
	pending: 'В очереди',
	running: 'Выполняется',
	done: 'Готово',
	failed: 'Ошибка',
	cancelled: 'Отменено',
};

export const CARD_SOURCE_LABELS: Record<string, string> = {
	sms: 'SMS',
	push: 'Push',
	phone_lookup: 'Авто (тел.)',
	pan_resolve: 'Авто (PAN)',
	balance_fetch: 'Авто (баланс)',
	korzinka_balance: 'Авто (баланс)',
};

const CARD_SOURCE_ALIASES: Record<string, string> = {
	korzinka_balance: 'balance_fetch',
};

const CARD_SOURCE_DISPLAY_ORDER = ['sms', 'push', 'phone_lookup', 'pan_resolve', 'balance_fetch'];

function normalizeCardSourceTag(tag: string): string {
	return CARD_SOURCE_ALIASES[tag] ?? tag;
}

function sortCardSourceTagsForDisplay(tags: string[]): string[] {
	return [...tags].sort((a, b) => {
		const ai = CARD_SOURCE_DISPLAY_ORDER.indexOf(a);
		const bi = CARD_SOURCE_DISPLAY_ORDER.indexOf(b);
		const ar = ai === -1 ? CARD_SOURCE_DISPLAY_ORDER.length : ai;
		const br = bi === -1 ? CARD_SOURCE_DISPLAY_ORDER.length : bi;
		if (ar !== br) return ar - br;
		return a.localeCompare(b);
	});
}

export type CardFillJobResult = {
	cards?: Array<{
		number?: string | null;
		maskedPan?: string | null;
		mask?: string | null;
		owner?: string | null;
		bankName?: string | null;
	}>;
	pan?: string;
	discovered?: Array<{ pan: string; owner?: string | null; bankName?: string | null }>;
	expire?: string | null;
	owner?: string | null;
	bankName?: string | null;
	balance?: number | null;
	skipReason?: string;
};

export function formatCardSources(sources?: string[]): string | null {
	if (!sources?.length) return null;
	const seen = new Set<string>();
	const normalized: string[] = [];
	for (const tag of sources) {
		const key = normalizeCardSourceTag(tag);
		if (seen.has(key)) continue;
		seen.add(key);
		normalized.push(key);
	}
	const ordered = sortCardSourceTagsForDisplay(normalized);
	const labels = ordered.map(s => CARD_SOURCE_LABELS[s] ?? s);
	return labels.join(' · ');
}

const cardBalanceMoney = new Intl.NumberFormat('en-US', { style: 'decimal', maximumFractionDigits: 0 });

/** Single-line balance display: amount, optional balanceDate, optional fetch timestamp. */
export function formatCardBalanceMeta(card: Pick<ICard, 'balance' | 'balanceDate' | 'balanceFetchedAt'>): string | null {
	const parts: string[] = [];
	if (card.balance != null) {
		parts.push(cardBalanceMoney.format(card.balance));
		if (card.balanceDate) {
			parts.push(card.balanceDate);
		}
	} else if (!card.balanceFetchedAt) {
		return null;
	}
	if (card.balanceFetchedAt) {
		parts.push(`обновлено ${formatDateTime(card.balanceFetchedAt)}`);
	}
	if (parts.length === 0) {
		return null;
	}
	if (card.balance == null && card.balanceFetchedAt) {
		return `— · ${parts.join(' · ')}`;
	}
	return parts.join(' · ');
}

export function formatDateSource(dateSource: string | null | undefined): string | null {
	if (!dateSource?.trim()) return null;
	return CARD_SOURCE_LABELS[dateSource] ?? dateSource;
}

export function formatDateTime(value: string | number | null | undefined): string {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
}

/** Short balance fetch time for tree UI: `14.09 19:36` */
export function formatBalanceFetchedShort(value: string | number | null | undefined): string {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	const dayMonth = date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit' });
	const time = date.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
	return `${dayMonth} ${time}`;
}

export function formatDuration(ms: number): string {
	if (ms < 0 || !Number.isFinite(ms)) return '—';
	const totalSec = Math.round(ms / 1000);
	if (totalSec < 60) return `${totalSec}с`;
	const min = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	if (min < 60) return sec > 0 ? `${min}м ${sec}с` : `${min}м`;
	const hr = Math.floor(min / 60);
	const remMin = min % 60;
	return remMin > 0 ? `${hr}ч ${remMin}м` : `${hr}ч`;
}

export function jobDurationMs(job: ICardFillJob): number | null {
	if (!job.finishedAt) return null;
	const start = new Date(job.createdAt).getTime();
	const end = new Date(job.finishedAt).getTime();
	if (Number.isNaN(start) || Number.isNaN(end)) return null;
	return end - start;
}

export const CARD_FILL_SKIP_REASON_LABELS: Record<string, string> = {
	brute_timeout: 'Лимит времени брута',
	pan_not_found: 'PAN не найден',
	no_combinations: 'Нет комбинаций',
	missing_proxy: 'Нет прокси',
	pan_resolve_disabled: 'PAN resolve отключён',
	disabled: 'Модуль отключён',
	date_check_disabled: 'Проверка срока отключена',
	date_check_humo_disabled: 'Поиск даты HUMO/VISA отключён',
	date_check_uzcard_disabled: 'Поиск даты UZCARD отключён',
	pan_resolve_uzcard_disabled: 'Подбор PAN UZCARD отключён',
	no_pan: 'Нет PAN в задании',
	pan_rejected: 'PAN отклонён',
	pan_resolve_skipped: 'PAN resolve пропущен',
	sms_otp_timeout: 'Таймаут SMS-кода',
	token_invalid: 'Токен карты недействителен',
};

export type CardFillJobOutcome = 'success' | 'partial' | 'empty' | 'failed' | 'cancelled' | 'active';

export type CardFillHistoryFilter = 'all' | 'success' | 'unsuccessful';

export const CARD_FILL_JOB_OUTCOME_LABELS: Record<CardFillJobOutcome, string> = {
	success: 'Успех',
	partial: 'Частично',
	empty: 'Не найдено',
	failed: 'Ошибка',
	cancelled: 'Отменено',
	active: 'Активно',
};

export type CardFillJobLike = Pick<
	ICardFillJob,
	'jobType' | 'status' | 'error' | 'result' | 'createdAt' | 'finishedAt'
>;

export function formatSkipReason(skipReason: string): string {
	return CARD_FILL_SKIP_REASON_LABELS[skipReason] ?? skipReason;
}

export function parseCardFillJobResult(raw: unknown): CardFillJobResult | null {
	if (!raw || typeof raw !== 'object') return null;
	return raw as CardFillJobResult;
}

function hasMeaningfulResult(job: CardFillJobLike, result: CardFillJobResult): boolean {
	switch (job.jobType) {
		case 'phone_lookup':
			return (result.cards?.length ?? 0) > 0;
		case 'date_check':
			return Boolean(result.expire?.trim() || result.owner?.trim() || result.bankName?.trim());
		case 'pan_resolve':
			return Boolean(result.pan?.trim()) || (result.discovered?.length ?? 0) > 0;
		case 'balance_fetch':
			return result.balance != null;
		default:
			return false;
	}
}

export function getJobOutcome(job: CardFillJobLike): CardFillJobOutcome {
	if (job.status === 'pending' || job.status === 'running') return 'active';
	if (job.status === 'failed') return 'failed';
	if (job.status === 'cancelled') return 'cancelled';

	const result = parseCardFillJobResult(job.result);
	if (result?.skipReason) {
		return (result.discovered?.length ?? 0) > 0 ? 'partial' : 'empty';
	}
	if (!result) {
		return job.error ? 'failed' : 'empty';
	}
	return hasMeaningfulResult(job, result) ? 'success' : 'empty';
}

export function getJobOutcomeLabel(job: CardFillJobLike): string {
	return CARD_FILL_JOB_OUTCOME_LABELS[getJobOutcome(job)];
}

export function isUnsuccessfulJobOutcome(outcome: CardFillJobOutcome): boolean {
	return outcome === 'empty' || outcome === 'partial' || outcome === 'failed' || outcome === 'cancelled';
}

export function filterHistoryJobs<T extends CardFillJobLike>(
	jobs: T[],
	filter: CardFillHistoryFilter,
): T[] {
	if (filter === 'all') return jobs;
	return jobs.filter(job => {
		const outcome = getJobOutcome(job);
		if (filter === 'success') return outcome === 'success';
		return isUnsuccessfulJobOutcome(outcome);
	});
}

function formatDiscoveredPanLine(entry: { pan: string; owner?: string | null; bankName?: string | null }): string {
	const parts = [entry.pan];
	if (entry.owner?.trim()) parts.push(entry.owner.trim());
	if (entry.bankName?.trim()) parts.push(entry.bankName.trim());
	return parts.join(' · ');
}

export function formatJobResultDetails(job: CardFillJobLike): string[] {
	const result = parseCardFillJobResult(job.result);
	if (!result) {
		return job.error ? [job.error] : [];
	}

	const lines: string[] = [];
	if (result.skipReason) {
		lines.push(`Пропуск: ${formatSkipReason(result.skipReason)}`);
	}

	switch (job.jobType) {
		case 'phone_lookup': {
			for (const card of result.cards ?? []) {
				const number = card.number?.trim() || card.maskedPan?.trim() || card.mask?.trim();
				if (!number) continue;
				const parts = [number];
				if (card.owner?.trim()) parts.push(card.owner.trim());
				if (card.bankName?.trim()) parts.push(card.bankName.trim());
				lines.push(parts.join(' · '));
			}
			break;
		}
		case 'date_check': {
			if (result.expire?.trim()) lines.push(`Срок: ${result.expire.trim()}`);
			if (result.owner?.trim()) lines.push(`ФИО: ${result.owner.trim()}`);
			if (result.bankName?.trim()) lines.push(`Банк: ${result.bankName.trim()}`);
			break;
		}
		case 'pan_resolve': {
			if (result.pan?.trim()) lines.push(`Целевой PAN: ${result.pan.trim()}`);
			for (const entry of result.discovered ?? []) {
				if (entry.pan?.trim()) lines.push(formatDiscoveredPanLine(entry));
			}
			break;
		}
		case 'balance_fetch': {
			if (result.balance != null) lines.push(`Баланс: ${result.balance}`);
			break;
		}
	}

	return lines;
}

export function readJobPayloadString(payload: unknown, key: string): string | null {
	if (!payload || typeof payload !== 'object') return null;
	const value = (payload as Record<string, unknown>)[key];
	return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function summarizeJobResult(job: ICardFillJob): string | null {
	const result = parseCardFillJobResult(job.result);
	if (!result) {
		if (job.error) return job.error;
		return null;
	}
	if (result.skipReason) return `Пропуск: ${formatSkipReason(result.skipReason)}`;

	switch (job.jobType) {
		case 'phone_lookup': {
			const count = result.cards?.length ?? 0;
			return count > 0 ? `Найдено карт: ${count}` : 'Карты не найдены';
		}
		case 'date_check': {
			const parts: string[] = [];
			if (result.expire) parts.push(`срок: ${result.expire}`);
			if (result.owner) parts.push(`ФИО: ${result.owner}`);
			if (result.bankName) parts.push(`банк: ${result.bankName}`);
			return parts.length > 0 ? parts.join(' · ') : 'Данные не получены';
		}
		case 'pan_resolve': {
			if (result.pan) return `PAN: ${result.pan}`;
			const discovered = result.discovered?.length ?? 0;
			return discovered > 0 ? `Найдено PAN: ${discovered}` : 'PAN не найден';
		}
		case 'balance_fetch': {
			if (result.balance != null) return `Баланс: ${result.balance}`;
			return result.skipReason ? `Пропуск: ${formatSkipReason(result.skipReason)}` : 'Баланс не получен';
		}
		default:
			return null;
	}
}

export function filterJobsByCardId(jobs: ICardFillJob[] | undefined, cardId: number): ICardFillJob[] {
	if (!jobs?.length) return [];
	return jobs
		.filter(
			j =>
				j.cardId === cardId &&
				(j.jobType === 'date_check' || j.jobType === 'pan_resolve' || j.jobType === 'balance_fetch'),
		)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getLatestBalanceFetchJob(
	jobs: ICardFillJob[] | undefined,
	cardId: number,
): ICardFillJob | null {
	return getLatestCardJob(jobs, cardId, 'balance_fetch');
}

export function hasActiveBalanceFetchJob(jobs: ICardFillJob[] | undefined, cardId: number): boolean {
	const job = getLatestBalanceFetchJob(jobs, cardId);
	return isJobActive(job);
}

export function filterJobsByPhoneId(jobs: ICardFillJob[] | undefined, phoneId: number): ICardFillJob[] {
	if (!jobs?.length) return [];
	return jobs
		.filter(j => j.phoneId === phoneId && j.jobType === 'phone_lookup')
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getLatestCardJob(
	jobs: ICardFillJob[] | undefined,
	cardId: number,
	jobType?: string,
): ICardFillJob | null {
	const filtered = filterJobsByCardId(jobs, cardId).filter(j => !jobType || j.jobType === jobType);
	return filtered[0] ?? null;
}

export function getLatestPhoneLookupJob(jobs: ICardFillJob[] | undefined, phoneId: number): ICardFillJob | null {
	const filtered = filterJobsByPhoneId(jobs, phoneId);
	return filtered[0] ?? null;
}

export function isJobActive(job: ICardFillJob | null | undefined): boolean {
	return job?.status === 'pending' || job?.status === 'running';
}

export function hasActiveCardJob(jobs: ICardFillJob[] | undefined, cardId: number): boolean {
	return filterJobsByCardId(jobs, cardId).some(
		j => j.jobType !== 'balance_fetch' && isJobActive(j),
	);
}

export function isLatestCardJobFailed(jobs: ICardFillJob[] | undefined, cardId: number): boolean {
	const latest = filterJobsByCardId(jobs, cardId)[0];
	return latest?.status === 'failed';
}

export type CardJobBadgeTone = 'active' | 'failed' | 'done' | null;

export function getCardJobBadgeTone(jobs: ICardFillJob[] | undefined, cardId: number): CardJobBadgeTone {
	if (hasActiveCardJob(jobs, cardId)) return 'active';
	const latest = filterJobsByCardId(jobs, cardId)[0];
	if (!latest) return null;
	if (latest.status === 'failed') return 'failed';
	if (latest.status === 'done') return 'done';
	return null;
}

export function getCardJobStatusLabel(jobs: ICardFillJob[] | undefined, cardId: number): string | null {
	const active = filterJobsByCardId(jobs, cardId).find(isJobActive);
	if (active) return CARD_FILL_JOB_STATUS_LABELS[active.status] ?? active.status;
	const latest = filterJobsByCardId(jobs, cardId)[0];
	if (!latest) return null;
	if (latest.status === 'failed') return 'Ошибка вбива';
	if (latest.status === 'done') {
		const dur = jobDurationMs(latest);
		return dur != null ? `Готово · ${formatDuration(dur)}` : 'Готово';
	}
	return null;
}

export function getPhoneLookupStatusLabel(jobs: ICardFillJob[] | undefined, phoneId: number): string | null {
	const active = filterJobsByPhoneId(jobs, phoneId).find(isJobActive);
	if (active) return CARD_FILL_JOB_STATUS_LABELS[active.status] ?? active.status;
	const latest = getLatestPhoneLookupJob(jobs, phoneId);
	if (!latest) return null;
	if (latest.status === 'failed') return latest.error ? `Ошибка: ${latest.error}` : 'Ошибка вбива';
	if (latest.status === 'done') {
		const summary = summarizeJobResult(latest);
		const dur = jobDurationMs(latest);
		const durPart = dur != null ? formatDuration(dur) : null;
		return [summary, durPart].filter(Boolean).join(' · ') || 'Готово';
	}
	return null;
}
